// MACHINERY V2 (Stroje) — Dokumenty tab (Manuals & Software) (M2.5).
//
// Spec: data package §6 + ticket §5 #6 / §7 fixes 2 and 4. Reference impl:
// design_handoff_machinery_v3/machinery-profile-tabs.jsx (`DocsTab`,
// `DocsExtras`); artboards 1003 (list), 1004 (upload), 1005 (empty + rename),
// 1014 (phone). Downloads + mutations live in useDocsTabActions.ts, the row in
// docsRows.tsx, the dialogs in DocsUploadDialogV2 / DocsRenameDialogV2.
//
// THE ADD BUTTON IS UNGATED, AND THAT IS PARITY, NOT AN OVERSIGHT. §6 records
// that create has no client PermissionGuard while the server route is Machinery
// READ-gated for a mutating write. Ticket §7 files that as a backend follow-up
// and explicitly not a v2 blocker, so v2 renders what v1 renders. Edit is Write
// and Delete is Admin, both matching DocumentItem.tsx:118 and :125.
//
// FIX 2 (size null -> "—") lives in tabFormat.ts; FIX 4 (the "100" that should
// say 50) lives in docsNameField.tsx. Both headers carry the citation.
//
// THE LIST IS machineId-GUARDED (M2.5 review finding R1).
// `customerMachineDocuments.documents` is an app-wide slot and nothing clears it
// on mount (customerMachineDocumentsSlice.ts:61-62 simply assigns the newest
// payload — personally read in this worktree, ticket process rule (a)). So
// between navigating machine A -> B and B's response landing, this tab rendered
// A's documents under B's hero, each row carrying a DOWNLOAD and an Admin
// DELETE. A
// Document carries no machineId of its own to compare (models/
// machineDocumentsModel.tsx:9-21), so ownership of the slot is tracked per
// settled request by useMachineScope — the same guard MachineProfileV2.tsx:137-142
// applies to `customerMachinery.machine` and SpecificationsTabV2.tsx:86 applies
// to `selectedModel`, and now the REQUIRED pattern for any v2 surface reading an
// app-wide slice keyed by machine. NOT a slice reset: this slice is shared with
// the live v1 pages.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import usePermission from "../../../../../shared/hooks/usePermission";
import { V2_PHONE, useV2MediaQuery } from "../../useV2MediaQuery";
import { Modules } from "../../../../../core/models/PermissionModels";
import { getDocuments } from "../../../customerMachineDocumentsSlice";
import { Document } from "../../../models/machineDocumentsModel";
import { FleetMenuAction } from "../../fleet/FleetCardMenu";
import RetryNotice from "../../feedback/RetryNotice";
import { TabCard, TabEmpty, TabIntro } from "./tabAtoms";
import { SkeletonList } from "./tabSkeletons";
import { DocRow } from "./docsRows";
import { useMachineScope } from "./useMachineScope";
import { useDocsTabActions } from "./useDocsTabActions";
import DocsUploadDialogV2 from "./DocsUploadDialogV2";
import DocsRenameDialogV2 from "./DocsRenameDialogV2";

export interface DocumentsTabV2Props {
  machineId: string;
}

const DocumentsTabV2: React.FC<DocumentsTabV2Props> = ({ machineId }) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("myMachinery");
  // F-A: the module's ONE phone predicate, evaluated as the query the CSS
  // evaluates. Identical to `innerWidth < 768` at every integer width.
  const isPhone = useV2MediaQuery(V2_PHONE);
  const { canWrite, canAdmin } = usePermission(Modules.Machinery);

  const documents = useAppSelector((s) => s.customerMachineDocuments.documents);
  const loading = useAppSelector((s) => s.customerMachineDocuments.loading);
  /** R1: which machine the shared `documents` slot currently belongs to, and
   *  whether THIS machine's own fetch rejected. */
  const { owns, failed, track } = useMachineScope(machineId);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<Document | null>(null);
  const aliveRef = useRef(true);
  const isAlive = useCallback(() => aliveRef.current, []);

  const load = useCallback(() => {
    // The failure is attributed to the machineId the request was FOR, so a late
    // rejection cannot light the retry notice on a machine the user moved to.
    track(machineId, dispatch(getDocuments(machineId)).unwrap());
  }, [dispatch, machineId, track]);

  useEffect(() => {
    aliveRef.current = true;
    load();
    return () => {
      aliveRef.current = false;
    };
  }, [load]);

  const { download, downloadAll, submitUpload, submitRename, requestDelete } =
    useDocsTabActions(machineId, load, isAlive);

  const buildActions = (doc: Document): FleetMenuAction[] => {
    const actions: FleetMenuAction[] = [];
    if (doc.file) {
      actions.push({
        id: "download",
        labelKey: "common:buttons.download",
        onSelect: () => download(doc),
      });
    }
    if (canWrite) {
      actions.push({
        id: "rename",
        labelKey: "common:buttons.edit",
        onSelect: () => setRenameTarget(doc),
      });
    }
    if (canAdmin) {
      actions.push({
        id: "delete",
        labelKey: "common:buttons.delete",
        danger: true,
        onSelect: () => requestDelete(doc),
      });
    }
    return actions;
  };

  // R1: `documents` is this machine's list only while the guard says so.
  // Anything else (another machine's response still in the slot, or nothing
  // fetched yet) renders the skeleton — never the previous machine's rows.
  const list = owns && documents ? documents : [];
  const withFiles = list.filter((d) => !!d.file).length;
  const menuLabel = translate("myMachinery:v2.tabs.docs.rowMenu");
  const showSkeleton = !owns && !failed;
  const showEmpty = owns && !loading && !failed && list.length === 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
      <TabIntro
        text={translate("myMachinery:v2.tabs.docs.intro")}
        count={translate("myMachinery:v2.tabs.docs.count", { count: list.length })}
      />

      {failed && (
        <RetryNotice
          title={translate("myMachinery:v2.tabs.docs.loadFailed")}
          hint={translate("myMachinery:v2.tabs.loadFailedHint")}
          onRetry={load}
          busy={loading}
        />
      )}

      {showSkeleton && (
        <TabCard>
          <SkeletonList rows={3} height={44} />
        </TabCard>
      )}

      {showEmpty && (
        <TabEmpty
          title={translate("myMachinery:v2.tabs.docs.emptyTitle")}
          hint={translate("myMachinery:v2.tabs.docs.emptyHint")}
          ctaLabel={translate("myMachinery:v2.tabs.docs.add")}
          onCta={() => setUploadOpen(true)}
        />
      )}

      {list.length > 0 && (
        <TabCard overflow="visible">
          {list.map((doc) => (
            <DocRow
              key={doc.id}
              document={doc}
              actions={buildActions(doc)}
              onDownload={download}
              menuLabel={menuLabel}
              phone={isPhone}
            />
          ))}
          {/* Footer bar — §6: it renders only when documents.length > 0.
              BOARD 22 pairs the two buttons as an equal 2-up row filling the
              width, rather than pinning one to each end with a flex spacer
              between them: at 390px the spacer leaves a wide dead gap and the
              primary CTA ends up as a small tile in the corner. Above 768 the
              artboard-1003 end-pinned row is unchanged. */}
          <div
            style={
              isPhone
                ? {
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                    gap: 8,
                    padding: "10px 12px",
                    borderTop: "1px solid var(--border)",
                    background: "var(--bg-soft)",
                  }
                : {
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 14px",
                    borderTop: "1px solid var(--border)",
                    background: "var(--bg-soft)",
                    flexWrap: "wrap",
                  }
            }
          >
            <button
              type="button"
              className="btn"
              onClick={() => downloadAll(list)}
              disabled={withFiles === 0}
              style={
                isPhone
                  ? { whiteSpace: "nowrap", width: "100%", justifyContent: "center" }
                  : { whiteSpace: "nowrap" }
              }
            >
              {translate("myMachinery:v2.tabs.docs.downloadAll", { count: withFiles })}
            </button>
            {!isPhone && <span style={{ flex: 1 }} />}
            <button
              type="button"
              className="btn btn-mod"
              onClick={() => setUploadOpen(true)}
              style={
                isPhone
                  ? { whiteSpace: "nowrap", width: "100%", justifyContent: "center" }
                  : { whiteSpace: "nowrap" }
              }
            >
              <span aria-hidden style={{ fontSize: 15, lineHeight: 1, marginRight: 2 }}>
                +
              </span>
              {translate("myMachinery:v2.tabs.docs.add")}
            </button>
          </div>
        </TabCard>
      )}

      <DocsUploadDialogV2
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onSubmit={submitUpload}
      />

      {renameTarget && (
        <DocsRenameDialogV2
          document={renameTarget}
          open
          onOpenChange={(open) => {
            if (!open) setRenameTarget(null);
          }}
          onSubmit={async (id, model) => {
            await submitRename(id, model);
            setRenameTarget(null);
          }}
        />
      )}
    </div>
  );
};

export default DocumentsTabV2;
